import React from 'react';
import {
  Stack,
  Text,
  Input,
  Select,
  Checkbox,
  Button,
  ControlLink,
  Overlay,
  SelectableOption,
  Clickable,
  LoadingBalls
} from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { hrefFor } from '../../routers';
import { tc } from '../../lib/trackingContext';
import { useModaNetworkForm } from './useModaNetworkForm';
import './ModaNetworkForm.scss';

type Props = { onSuccess: () => void; onClose: () => void };

const BUSINESS_TYPE_OPTIONS: SelectableOption[] = [
  {
    label: 'Personal Stylist',
    value: 'Personal Stylist'
  },
  {
    label: 'Bridal Stylist',
    value: 'Bridal Stylist'
  },
  {
    label: 'Celebrity Stylist',
    value: 'Celebrity Stylist'
  },
  {
    label: 'Costume Designer',
    value: 'Costume Designer'
  },
  {
    label: 'Other',
    value: 'Other'
  }
];

const EST_CLIENT_BOOK_VAL_OPTIONS: SelectableOption[] = [
  {
    label: 'Under $50,000',
    value: 'Under $50,000'
  },
  {
    label: '$50,000 - $100,000',
    value: '$50,000 - $100,000'
  },
  {
    label: '$100,000 - $250,000',
    value: '$100,000 - $250,000'
  },
  {
    label: '$250,000 - $500,000',
    value: '$250,000 - $500,000'
  },
  {
    label: '$500,000+',
    value: '$500,000+'
  }
];

const NUM_OF_ACTIVE_CLIENTS_OPTIONS: SelectableOption[] = [
  {
    label: 'Under 5 clients',
    value: 'Under 5 clients'
  },
  {
    label: '5-10 clients',
    value: '5-10 clients'
  },
  {
    label: '10-20 clients',
    value: '10-20 clients'
  },
  {
    label: '20-50 clients',
    value: '20-50 clients'
  },
  {
    label: '50-100 clients',
    value: '50-100 clients'
  },
  {
    label: '100+ clients',
    value: '100+ clients'
  }
];

export const ModaNetworkForm: React.FC<Props> = ({ onSuccess, onClose }) => {
  const { handleSubmit, formErrors, error, loading } = useModaNetworkForm({ onSuccess });

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(event);
    tc.track('Moda Network Form Submit Attempted', {
      addToTrackingEvent: {
        label: 'Submit',
        location: 'Moda Network Form'
      }
    });
  };

  const handleModalClose = () => {
    onClose();
    tc.track('Moda Network Form Closed', {
      addToTrackingEvent: {
        label: 'X',
        location: 'Moda Network Form Close button'
      }
    });
  };

  return (
    <form className="ModaNetworkForm" onSubmit={handleFormSubmit}>
      <Clickable className="ModaNetworkForm__close" aria-label="Close" onClick={handleModalClose}>
        <ExitIcon />
      </Clickable>
      <Stack space={5}>
        <Stack space={4}>
          <Text className="ModaNetworkForm__title" treatment="h3" family="serif">
            Apply Now
          </Text>
          <Text className="ModaNetworkForm__description">
            MODA Network provides a unique space for Stylists & Costume Designers to grow their
            business. Provide your detailed profile below and our team will review and be in touch
            soon.
          </Text>
          <Input label="Full Name" name="name" error={formErrors.name?.message} />
          <Input label="Email" name="email" error={formErrors.email?.message} />
          <Input label="Phone Number" name="phone" error={formErrors.phone?.message} />
          <Input
            label="Where are you located? (City, Country)"
            name="location"
            error={formErrors.location?.message}
          />
          <Input
            label="Business type"
            name="business"
            placeholder="Select an option"
            error={formErrors.business?.message}
          >
            <Select label="" options={BUSINESS_TYPE_OPTIONS} />
          </Input>

          <Input
            label="Website"
            name="link"
            placeholder="https://yourwebsite.com"
            error={formErrors.link?.message}
          />
          <Input
            label="Primary Social Media Handle(s)"
            name="primarySocialMediaHandle"
            placeholder="@yourhandle"
            error={formErrors.primarySocialMediaHandle?.message}
          />

          <Input
            label="Links to Notable Press Features (Comma-separated)"
            name="press"
            error={formErrors.press?.message}
          />

          <Input
            label="Estimated Annual Client Book Value"
            name="estimatedClientBookValue"
            placeholder="Select an option"
            error={formErrors.estimatedClientBookValue?.message}
          >
            <Select label="" options={EST_CLIENT_BOOK_VAL_OPTIONS} />
          </Input>

          <Input
            label="Number of Active Clients"
            name="numOfActiveClients"
            placeholder="Select an option"
            error={formErrors.numOfActiveClients?.message}
          >
            <Select label="" options={NUM_OF_ACTIVE_CLIENTS_OPTIONS} />
          </Input>

          <Input
            label="Special Services You Offer (e.g. custom designs, wardrobe curation, sourcing rare pieces)"
            name="specialServices"
            error={formErrors.specialServices?.message}
          />

          <Input
            label="Other Stylist or Affiliate Programs You Currently Participate In"
            name="affiliatePrograms"
            error={formErrors.affiliatePrograms?.message}
          />

          <Input
            label="Tell us a little about yourself and your business (Include your specialty, client base, style approach, and top brands)"
            name="message"
            error={formErrors.message?.message}
          />
        </Stack>
        <Stack space={4}>
          <Checkbox name="terms">
            <>
              I accept the{' '}
              <ControlLink to={hrefFor.TermsAndConditionsPage()}>Terms & Conditions</ControlLink>
            </>
          </Checkbox>
          <Checkbox name="subscribe">Sign me up for special access and updates</Checkbox>
          {error && <Text color="code-red">{error.message}</Text>}
        </Stack>
        <Button type="submit" className="ModaNetworkForm__submit">
          Submit
        </Button>
      </Stack>
      <Overlay show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Sending</Text>
      </Overlay>
    </form>
  );
};
